import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { uploadFileWithProgress } from '@actions/file';

import FileDropzone from '@components/FileDropzone/FileDropzone';
import MainTitle from '@components/MainTitle';
import { Progress } from '@components/Progress';

import { isDevelopment } from '@constants/env';

import {
  useSetDocumentsUploaded,
  useUploadClientRecords,
} from '@hooks/api/document';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { queuePromises } from '@utils/queuePromises';

import type {
  ExpectedUploadHeaders,
  PartModel,
  UploadClientRecordsModelInput,
} from 'generated/types';

import { RecordsUpdateStyle } from '../ReportContent/ReportContent.style';

const messages = defineMessages({
  title: {
    id: 'uploadRecordsContent.title',
    defaultMessage: 'Upload Records',
  },
  subtitle: {
    id: 'uploadRecordsContent.subtitle',
    defaultMessage: 'Drag and drop your records here and click Upload',
  },
  text: {
    id: 'uploadRecordsContent.text',
    defaultMessage:
      'A VITALL representative will be notified and your records will be extracted and organized into your profile.',
  },
  error: {
    id: 'uploadRecordsContent.error',
    defaultMessage:
      'An error occurred while attempting to upload records. Please try again later.',
  },
  success: {
    id: 'uploadRecordsContent.success',
    defaultMessage: 'Records have been successfully uploaded',
  },
  accepted: {
    id: 'uploadRecordsContent.fileTypes',
    defaultMessage: 'Accepted Files: jpeg, jpg, png, pdf, xml, docx, xlsx, zip',
  },
});

const UploadRecordsContent: React.FC<{
  /**
   * Callback for when the submit button is clicked
   */
  onSubmit?: () => void;
}> = ({ onSubmit }) => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { activeProfile },
  } = useSession();
  const vitall_patient_id = activeProfile?.vitall_patient_id;

  const [uploadRecords] = useUploadClientRecords();
  const [setDocumentsUploaded] = useSetDocumentsUploaded();
  const [progress, setProgress] = useState<number>(0);

  async function handleSubmit(files: DroppedFile[]) {
    if (!vitall_patient_id) {
      throw Error('No vitall patient ID');
    }
    setProgress(10);
    const documents = files.map(({ baseName, name, size }) => ({
      name,
      description: `${baseName} record for patient ${vitall_patient_id}`,
      size,
    }));
    try {
      // 1. Add records to DB
      const recordsResponse = await uploadRecords({
        input: {
          vitall_patient_id,
          documents,
        },
      });
      const records = recordsResponse.data?.uploadClientRecords ?? [];
      if (records.length) {
        setProgress((prev) => prev + 23);
        const uploads = records.reduce<
          {
            documentId: string;
            file: File;
            isMultipart: boolean;
            multipartUploadId?: string | null;
            parts: PartModel[];
            expectedHeaders?: ExpectedUploadHeaders | null;
          }[]
        >((acc, curr, index) => {
          const { document_id, uploadData } = curr;
          if (!document_id) {
            return acc;
          }
          const { isMultipart, multipartUploadId, parts, expectedHeaders } =
            uploadData;
          acc.push({
            documentId: document_id,
            file: files[index],
            isMultipart,
            multipartUploadId,
            parts,
            expectedHeaders,
          });
          return acc;
        }, []);

        // 2. Add records to S3
        const uploadedParts = await queuePromises(
          uploads,
          async ({ file, parts, expectedHeaders }) => {
            return uploadFileWithProgress(
              file,
              parts,
              undefined,
              expectedHeaders
            );
          }
        );
        setProgress((prev) => prev + 33);

        // 3. Notify the database that the records were added to S3
        const uploadedResponse = await setDocumentsUploaded({
          input: {
            documents: uploads.map<UploadClientRecordsModelInput>(
              ({ documentId, isMultipart, multipartUploadId }, index) => {
                const partSection = uploadedParts[index];
                const parts =
                  isMultipart && partSection instanceof Array
                    ? partSection
                    : [];
                return {
                  documentId,
                  multipartUploadInfo: {
                    isMultipart,
                    parts,
                    multipartUploadId,
                  },
                };
              }
            ),
          },
        });
        if (uploadedResponse.errors) {
          enqueueSnackbar({
            variant: 'error',
            message: formatMessage(messages.error),
          });
        } else if (uploadedResponse.data?.setDocumentsUploaded) {
          setProgress((prev) => prev + 33);
          enqueueSnackbar({
            variant: 'success',
            message: formatMessage(messages.success),
          });
        }
      }
    } catch (e) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    } finally {
      onSubmit?.();
    }
  }

  return (
    <RecordsUpdateStyle>
      <MainTitle title={formatMessage(messages.title)} />
      <p>{formatMessage(messages.subtitle)}</p>
      <p>{formatMessage(messages.accepted)}</p>

      <FileDropzone onUpload={handleSubmit} />
      {progress > 0 && <Progress value={progress} width="100%" />}

      <p>{formatMessage(messages.text)}</p>
    </RecordsUpdateStyle>
  );
};

export default UploadRecordsContent;
